"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocale } from "@/components/locale-provider";

type ConfirmOptions = {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type ConfirmState = ConfirmOptions & { resolve: (value: boolean) => void };

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useLocale();
  const [state, setState] = useState<ConfirmState | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const open = state !== null;

  const confirm = (options: ConfirmOptions) =>
    new Promise<boolean>((resolve) => {
      setState({ ...options, resolve });
    });

  const close = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 px-3 pt-3 pb-[max(0.75rem,var(--safe-area-inset-bottom,env(safe-area-inset-bottom)))] backdrop-blur-sm md:items-center md:p-3"
          onClick={() => close(false)}
        >
          <div
            className="glass-floating glass-specular w-full max-w-sm rounded-ios-lg p-5"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <p id="confirm-dialog-title" className="text-[16px] font-semibold text-ios-label">
                {state.title}
              </p>
              {state.message && (
                <p className="mt-1.5 text-[13px] text-ios-label-secondary">{state.message}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                ref={cancelRef}
                type="button"
                onClick={() => close(false)}
                className="press-shrink flex-1 rounded-full bg-ios-fill px-4 py-2.5 text-[14px] font-semibold text-ios-label"
              >
                {state.cancelLabel ?? t.confirmDialog.cancel}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                className={`glass-specular press-shrink flex-1 rounded-full px-4 py-2.5 text-[14px] font-semibold text-white ${
                  state.destructive ? "bg-ios-destructive" : "bg-ios-accent"
                }`}
              >
                {state.confirmLabel ?? t.confirmDialog.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within a ConfirmProvider");
  return ctx;
}
